import { Link, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { createUrl } from 'app/utils';
import { Button, Flex, Page, RichContent, SearchSelect, Section, Typography } from 'components';
import { ProductEnrollmentBox, ProductsCertificateDownload, ProductsProgress, useProducts } from 'features/products';

import { useCallback } from 'react';

const Wrapper = styled(Flex)`
  position: relative;
`;

const ContentWrapper = styled(Flex)`
  backdrop-filter: blur(6px);
`;

interface DetailsProps {
  paymentLink: string | false | null;
  apiFetch: (forceEnroll: boolean) => void;
}

const Details = ({ apiFetch, paymentLink }: DetailsProps) => {
  const { productSlug, productClassUuid } = useParams();

  const { productDetails, availableClasses, productEvolutionDetails } = useProducts();

  const getProductClassUuid = useCallback(
    (receivedProductClassUuid: string = '') => {
      return productClassUuid || receivedProductClassUuid || availableClasses?.current?.productClassUuid;
    },
    [availableClasses, productClassUuid]
  );

  const isEnrolled = productDetails?.enrolled;

  // console.log(isEnrolled, availableClasses?.current?.allowEnrollments, paymentLink);

  return (
    <Page title={productDetails?.product?.name}>
      <Wrapper
        justifyContent="center"
        alignItems="center"
        backgroundImage={`url(${productDetails?.product.coverImageUrl})`}
        backgroundSize="cover"
        position="relative"
      >
        <Section flexDirection="column" alignItems="flex-end">
          {isEnrolled && !!availableClasses?.availables?.length && availableClasses?.availables?.length > 1 && (
            <Flex flexGrow={0} mb={3}>
              <SearchSelect
                borderColor="white"
                name="itensPerPage"
                onChange={(event: React.ChangeEvent<HTMLSelectElement>) => {
                  if (event.target.value) {
                    window.location.href = `${createUrl('productClassDetails', [productSlug, event.target.value])}`;
                  }
                }}
                defaultValue={productClassUuid || availableClasses?.current?.productClassUuid}
              >
                {availableClasses.availables.map((availableClass) => (
                  <option key={`availableClass-${availableClass.productClassUuid}`} value={availableClass.productClassUuid}>
                    {availableClass.description}
                  </option>
                ))}
              </SearchSelect>
            </Flex>
          )}

          <Flex flexWrap={{ m: 'wrap', d: 'nowrap' }}>
            <ContentWrapper
              bg="black80"
              borderTopLeftRadius={4}
              borderBottomLeftRadius={4}
              borderTopRightRadius={{ m: 4, d: isEnrolled ? 0 : 4 }}
              borderBottomRightRadius={{ m: 4, d: isEnrolled ? 0 : 4 }}
              flexDirection="column"
              p={{ m: 4, d: 6 }}
              alignItems="flex-start"
            >
              {!!productDetails?.product.hat && (
                <Typography type="type3" color="gray_500" upper>
                  {productDetails?.product.hat}
                </Typography>
              )}

              <Typography type="type8" fontWeight="bold" color="white">
                {productDetails?.product.name}
              </Typography>

              {!!productDetails?.product.description && (
                <Flex flexGrow={0} mt={3}>
                  <RichContent currentTheme="dark" defaultType="type1">
                    {productDetails?.product.description}
                  </RichContent>
                </Flex>
              )}

              {isEnrolled ? (
                <>
                  <Link to={createUrl('contentScormDetails', [productSlug, getProductClassUuid()])}>
                    <Button type="button" mt={{ m: 4, d: 4 }}>
                      {productEvolutionDetails?.completionPercentage ? 'Continuar' : 'Iniciar'}
                    </Button>
                  </Link>

                  {productEvolutionDetails?.certificateIsAvailable && Boolean(productEvolutionDetails?.certificateAddress) && (
                    <Flex width={1} maxWidth={250} flexGrow={0} mt={3} flexDirection="column">
                      <ProductsCertificateDownload size="small" filePath={productEvolutionDetails?.certificateAddress} />
                    </Flex>
                  )}
                </>
              ) : (
                <Flex mt={4}>
                  <ProductEnrollmentBox paymentLinkUrl={paymentLink} apiFetch={apiFetch} hideDetails hidePriceIfFree />
                </Flex>
              )}
            </ContentWrapper>

            {isEnrolled && (
              <Flex
                borderTopLeftRadius={{ m: 4, d: 0 }}
                borderTopRightRadius={{ m: 4 }}
                borderBottomRightRadius={{ m: 4 }}
                borderBottomLeftRadius={{ m: 4, d: 0 }}
                width={{ m: '100%', d: 400 }}
                mt={{ m: 4, d: 0 }}
                flexGrow={0}
                bg="white"
                py={{ m: 20, t: 30, d: 40 }}
                flexDirection="column"
                alignItems="center"
                style={{ gap: 20 }}
                flexShrink={0}
              >
                <Typography as="span" type="type5" lineHeight="1.3" color="dark_350" fontWeight="bold">
                  Sua evolução
                </Typography>

                <ProductsProgress percent={productEvolutionDetails?.completionPercentage} />
              </Flex>
            )}
          </Flex>
        </Section>
      </Wrapper>
    </Page>
  );
};

export default Details;
